import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { ArrowLeft, Save, Eye, Plus, Trash2, Calendar, Clock, Users, CheckSquare, FileText, Lightbulb, ListTodo, CalendarDays, Loader2, AlertTriangle, ChevronDown, Edit3, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import { createEmptyPV } from './mock';
import { pvService, memberService, handleApiError, propositionsService } from '../services/api';
import { useToast } from '../hooks/use-toast';

// Composant pour les éléments draggables des points à l'ordre du jour
const SortablePointItem = ({ 
  point, 
  index, 
  totalPoints, 
  showNotes, 
  onPointChange, 
  onRemovePoint, 
  onToggleNotes, 
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  onToggleNoteTermine,
  editingNoteIndex,
  setEditingNoteIndex
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `point-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const notes = point.notes || [];

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`border border-gray-200 rounded-lg p-3 sm:p-4 space-y-3 bg-white ${isDragging ? 'shadow-lg' : ''}`}
    >
      <div className="flex gap-2 items-center">
        <div 
          {...attributes} 
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
          title="Glisser pour réorganiser"
        >
          <GripVertical className="w-4 h-4 text-gray-400" />
        </div>
        <span className="text-sm font-medium text-gray-500 min-w-[20px]">
          {index + 1}.
        </span>
        <Input
          value={point.titre}
          onChange={(e) => onPointChange(index, 'titre', e.target.value)}
          placeholder={`Point ${index + 1}...`}
          className="flex-1 border-green-200 focus:border-green-400 text-sm sm:text-base"
        />
        {totalPoints > 1 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRemovePoint(index)}
            className="border-red-200 text-red-600 hover:bg-red-50 p-1 sm:p-2 min-w-[32px]"
            data-testid={`remove-point-${index}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onToggleNotes(index)}
          className="border-blue-200 text-blue-600 hover:bg-blue-50 text-xs px-2 py-1 h-auto w-fit"
          data-testid={`toggle-notes-${index}`}
        >
          {showNotes[index] ? 'Masquer' : `Notes${notes.length > 0 ? ` (${notes.length})` : ''}`}
        </Button>
        
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id={`traite-${index}`}
            checked={point.traite}
            onChange={(e) => onPointChange(index, 'traite', e.target.checked)}
            className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
          />
          <Label 
            htmlFor={`traite-${index}`} 
            className={`text-sm cursor-pointer ${point.traite ? 'text-green-700 font-semibold' : 'text-gray-600'}`}
          >
            {point.traite ? 'Point traité' : 'Point à traiter'}
          </Label>
        </div>
      </div>
      
      {showNotes[index] && (
        <div className="mt-3 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium text-gray-700">Notes :</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAddNote(index)}
              className="border-green-200 text-green-600 hover:bg-green-50 text-xs px-2 py-1 h-auto"
              data-testid={`add-note-${index}`}
            >
              <Plus className="w-3 h-3 mr-1" /> Ajouter une note
            </Button>
          </div>
          
          {notes.length === 0 && (
            <p className="text-gray-400 text-xs italic">Aucune note pour ce point. Cliquez sur "Ajouter une note" pour commencer.</p>
          )}

          <div className="space-y-2">
            {notes.map((note, noteIdx) => (
              <div key={noteIdx} className={`border rounded-md p-3 transition-colors ${
                note.termine 
                  ? 'border-green-200 bg-green-50/40' 
                  : 'border-blue-100 bg-blue-50/30'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-semibold flex items-center gap-1 ${
                    note.termine ? 'text-green-600' : 'text-blue-700'
                  }`}>
                    <Calendar className="w-3 h-3" /> {note.date}
                    {note.termine && <span className="bg-green-200 text-green-800 px-1.5 py-0.5 rounded text-[10px] ml-1">Terminé</span>}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onToggleNoteTermine(index, noteIdx)}
                      className={`text-xs px-2 py-0.5 h-auto ${
                        note.termine 
                          ? 'text-orange-600 hover:bg-orange-50' 
                          : 'text-green-600 hover:bg-green-50'
                      }`}
                      data-testid={`toggle-note-${index}-${noteIdx}`}
                    >
                      {note.termine ? 'Rouvrir' : 'Terminer'}
                    </Button>
                    {editingNoteIndex?.pointIndex === index && editingNoteIndex?.noteIndex === noteIdx ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingNoteIndex(null)}
                        className="text-green-600 hover:bg-green-50 text-xs px-2 py-0.5 h-auto"
                      >
                        OK
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingNoteIndex({ pointIndex: index, noteIndex: noteIdx })}
                        className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 p-1 h-auto"
                        data-testid={`edit-note-${index}-${noteIdx}`}
                      >
                        <Edit3 className="w-3 h-3" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteNote(index, noteIdx)}
                      className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-1 h-auto"
                      data-testid={`delete-note-${index}-${noteIdx}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                {editingNoteIndex?.pointIndex === index && editingNoteIndex?.noteIndex === noteIdx ? (
                  <Textarea
                    value={note.texte}
                    onChange={(e) => onUpdateNote(index, noteIdx, e.target.value)}
                    className="border-blue-200 focus:border-blue-400 text-sm"
                    rows={3}
                    autoFocus
                  />
                ) : (
                  <p className={`text-sm whitespace-pre-wrap ${note.termine ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{note.texte || <span className="italic text-gray-400">Note vide</span>}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PVForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const [pv, setPV] = useState(createEmptyPV());
  const [memberPresence, setMemberPresence] = useState({});
  const [clubMembers, setClubMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showNotes, setShowNotes] = useState({}); // Track which points show notes
  const [editingNoteIndex, setEditingNoteIndex] = useState(null); // Track which note is being edited
  const [originalPV, setOriginalPV] = useState(null); // Track original PV for changes detection
  const [showExitConfirmation, setShowExitConfirmation] = useState(false); // Show exit confirmation modal
  const [editingTaskStatus, setEditingTaskStatus] = useState(null); // Track which task status is being edited

  // States for propositions individuelles
  const [nouveauProposition, setNouveauProposition] = useState({
    membre: '',
    contenu: ''
  });
  const [editingProposition, setEditingProposition] = useState(null); // Track which proposition is being edited

  // État pour le tri des tâches uniquement
  const [taskSort, setTaskSort] = useState('statut'); // 'statut', 'responsable', 'delai'
  const [taskSortDirection, setTaskSortDirection] = useState('asc'); // 'asc', 'desc'

  // États pour la sauvegarde automatique
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'

  // États pour les notifications de nouvelles propositions
  const [pendingPropositions, setPendingPropositions] = useState([]);
  const [showPropositionsNotification, setShowPropositionsNotification] = useState(false);
  const [lastPropositionsCheck, setLastPropositionsCheck] = useState(null);

  // Load members from API
  const loadMembers = async () => {
    try {
      const members = await memberService.getAll();
      // Convert member objects to names for compatibility
      const memberNames = members.map(member => 
        typeof member === 'string' ? member : member.name
      );
      setClubMembers(memberNames);
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur",
        description: `Impossible de charger les membres: ${errorMessage}`,
        variant: "destructive"
      });
      // Fallback to default members
      setClubMembers(['Lucile', 'Tania', 'Xavier', 'Oscar', 'Nicolas', 'Maxime']);
    }
  };

  // Load PV data
  // Load ongoing tasks from latest PV
  const loadOngoingTasks = async () => {
    try {
      const ongoingTasks = await pvService.getOngoingTasks();
      if (ongoingTasks.length > 0) {
        setPV(prev => ({
          ...prev,
          prochainEtapes: [
            ...ongoingTasks
          ]
        }));
        
        toast({
          title: "Tâches récupérées",
          description: `${ongoingTasks.length} tâche(s) en cours et en suspens du PV précédent ont été ajoutées.`,
        });
      }
    } catch (error) {
      console.error('Error loading ongoing tasks:', error);
    }
  };

  // Load untreated points from latest PV
  const loadUntreatedPoints = async () => {
    try {
      const untreatedPoints = await pvService.getUntreatedPoints();
      if (untreatedPoints.length > 0) {
        setPV(prev => {
          // Remove empty points from the initial PV
          const existingPoints = prev.pointsOrdreJour.filter(point => 
            point.titre && point.titre.trim()
          );
          
          // Avoid duplicates by checking title (case insensitive)
          const existingTitles = new Set(existingPoints.map(p => p.titre?.trim().toLowerCase()));
          const seenTitles = new Set();
          const newPoints = untreatedPoints.filter(newPoint => {
            const titleKey = newPoint.titre?.trim().toLowerCase();
            if (existingTitles.has(titleKey) || seenTitles.has(titleKey)) return false;
            seenTitles.add(titleKey);
            return true;
          });
          
          if (newPoints.length === 0) return prev;
          
          return {
            ...prev,
            pointsOrdreJour: [
              ...newPoints,
              ...existingPoints
            ]
          };
        });
        
        toast({
          title: "Points non traités récupérés",
          description: `${untreatedPoints.length} point(s) non traité(s) du PV précédent ont été repris.`,
        });
      }
    } catch (error) {
      console.error('Error loading untreated points:', error);
    }
  };

  // Load PV data
  const loadPV = async (pvId) => {
    try {
      console.log('Loading PV with ID:', pvId);
      const existingPV = await pvService.getById(pvId);
      console.log('PV loaded successfully:', existingPV);
      
      if (existingPV) {
        // Normalize points structure for backward compatibility
        let normalizedPV = { ...existingPV };
        
        if (normalizedPV.pointsOrdreJour) {
          normalizedPV.pointsOrdreJour = normalizedPV.pointsOrdreJour.map(point => {
            if (typeof point === 'string') {
              return {
                titre: point,
                traite: false,
                notes: []
              };
            } else if (typeof point === 'object' && point !== null) {
              // Migrate old remarques to notes if needed
              let notes = point.notes || [];
              if ((!notes || notes.length === 0) && point.remarques && point.remarques.trim()) {
                // Try to split old remarques by date patterns [DD/MM/YYYY] or [DD.MM.YYYY]
                const datePattern = /\[(\d{2}[/.]\d{2}[/.]\d{4})\]\s*/g;
                const rawText = point.remarques.trim();
                const parts = rawText.split(datePattern).filter(Boolean);
                
                if (parts.length >= 2) {
                  // Found date patterns - create notes from them
                  notes = [];
                  for (let i = 0; i < parts.length; i += 2) {
                    const date = parts[i];
                    const texte = (parts[i + 1] || '').trim();
                    if (texte) {
                      notes.push({ date, texte });
                    }
                  }
                }
                
                if (notes.length === 0) {
                  notes = [{ date: 'Historique', texte: rawText }];
                }
              }
              return {
                titre: point.titre || '',
                traite: point.traite || false,
                notes: notes
              };
            }
            return {
              titre: '',
              traite: false,
              notes: []
            };
          });
        }
        
        setPV(normalizedPV);
        setOriginalPV(normalizedPV); // Save original state for comparison
        
        // Initialiser les préférences de tri depuis le PV chargé
        if (normalizedPV.taskSort) {
          setTaskSort(normalizedPV.taskSort);
        }
        if (normalizedPV.taskSortDirection) {
          setTaskSortDirection(normalizedPV.taskSortDirection);
        }

        // Initialize member presence immediately after PV is loaded
        if (clubMembers.length > 0) {
          console.log('🔍 IMMEDIATE INITIALIZATION: Setting up member presence');
          console.log('normalizedPV.presents:', normalizedPV.presents);
          console.log('normalizedPV.excuses:', normalizedPV.excuses);
          
          const presence = {};
          clubMembers.forEach(member => {
            if (normalizedPV.presents && normalizedPV.presents.includes(member)) {
              presence[member] = 'present';
            } else if (normalizedPV.excuses && normalizedPV.excuses.includes(member)) {
              presence[member] = 'excuse';
            } else {
              presence[member] = 'absent';
            }
          });
          setMemberPresence(presence);
          console.log('✅ IMMEDIATE: Member presence set:', presence);
        }
      } else {
        console.log('No PV found with ID:', pvId);
        toast({
          title: "Erreur",
          description: "PV non trouvé",
          variant: "destructive"
        });
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error loading PV:', error);
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur",
        description: `Impossible de charger le PV: ${errorMessage}`,
        variant: "destructive"
      });
      navigate('/dashboard');
    }
  };

  // Fonction pour vérifier les nouvelles propositions (pour PV existants)
  const checkForNewPropositions = async () => {
    try {
      // Ne vérifier que pour les PV existants (pas les nouveaux)
      if (!id) return;
      
      console.log('Checking for new propositions...');
      const response = await propositionsService.getActiveSession();
      
      if (response.active_session && response.active_session.propositions_count > 0) {
        const sessionToken = response.active_session.session_token;
        const propositionsData = await propositionsService.listPropositions(sessionToken);
        const propositions = propositionsData.propositions || [];
        
        // Filter propositions that are not integrated yet AND not refused/validated
        const newPropositions = propositions.filter(prop => 
          !prop.integre_pv && prop.statut === 'en_attente'
        );
        
        // Check if we have propositions that weren't there in the last check
        if (newPropositions.length > 0) {
          const currentPropositionIds = newPropositions.map(p => p.id);
          const lastCheckIds = lastPropositionsCheck?.map(p => p.id) || [];
          
          // Only show notification if there are actually NEW propositions since last check
          const hasNewPropositions = currentPropositionIds.some(id => !lastCheckIds.includes(id));
          
          if (hasNewPropositions || !lastPropositionsCheck) {
            setPendingPropositions(newPropositions);
            setShowPropositionsNotification(true);
            console.log(`Found ${newPropositions.length} pending propositions (status: en_attente)`);
          }
          
          setLastPropositionsCheck(newPropositions);
        } else {
          // No pending propositions
          setPendingPropositions([]);
          setShowPropositionsNotification(false);
          setLastPropositionsCheck([]);
        }
      } else {
        // No active session
        setPendingPropositions([]);
        setShowPropositionsNotification(false);
        setLastPropositionsCheck([]);
      }
    } catch (error) {
      console.error('Error checking for new propositions:', error);
      // Silent fail - this is a background check
    }
  };

  // Intégration sélective des propositions en attente
  const integrateSelectedPropositions = async (selectedPropositionIds = null) => {
    try {
      const propositionsToIntegrate = selectedPropositionIds 
        ? pendingPropositions.filter(prop => selectedPropositionIds.includes(prop.id))
        : pendingPropositions; // Si pas de sélection, intégrer toutes

      if (propositionsToIntegrate.length === 0) {
        toast({
          title: "Aucune proposition",
          description: "Aucune proposition à intégrer",
          variant: "default"
        });
        return;
      }

      console.log(`Integrating ${propositionsToIntegrate.length} selected propositions`);
      
      // Convert propositions to ordre du jour points
      const propositionPoints = propositionsToIntegrate.map(prop => {
        const currentDate = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        return {
          titre: prop.titre,
          traite: false,
          notes: prop.description ? [{ date: currentDate, texte: prop.description }] : [],
          source: 'proposition',
          proposition_id: prop.id
        };
      });
      
      // Add to current PV points
      setPV(prevPV => ({
        ...prevPV,
        pointsOrdreJour: [...prevPV.pointsOrdreJour, ...propositionPoints]
      }));
      
      // Remove integrated propositions from pending list
      const remainingPropositions = pendingPropositions.filter(
        prop => !propositionsToIntegrate.some(intProp => intProp.id === prop.id)
      );
      setPendingPropositions(remainingPropositions);
      
      // Hide notification if no more pending propositions
      if (remainingPropositions.length === 0) {
        setShowPropositionsNotification(false);
      }
      
      // Show success notification
      toast({
        title: "Propositions intégrées",
        description: `${propositionsToIntegrate.length} proposition(s) ajoutée(s) à l'ordre du jour`,
        variant: "default"
      });
      
      return propositionsToIntegrate.length;
    } catch (error) {
      console.error('Error integrating selected propositions:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'intégrer les propositions",
        variant: "destructive"
      });
      return 0;
    }
  };

  // Gestion des décisions individuelles sur les propositions
  const handleIndividualPropositionDecision = async (propositionId, statut) => {
    try {
      console.log(`Making decision on proposition ${propositionId}: ${statut}`);
      
      // Faire la décision via l'API
      await propositionsService.makePropositionDecision(propositionId, statut);
      
      // Si validé, intégrer la proposition
      if (statut === 'validé') {
        await integrateSelectedPropositions([propositionId]);
      } else {
        // Si refusé, simplement enlever de la liste des propositions en attente
        const remainingPropositions = pendingPropositions.filter(prop => prop.id !== propositionId);
        setPendingPropositions(remainingPropositions);
        
        // Hide notification if no more pending propositions
        if (remainingPropositions.length === 0) {
          setShowPropositionsNotification(false);
        }
        
        toast({
          title: "Proposition refusée",
          description: "La proposition a été refusée avec succès",
          variant: "default"
        });
      }
      
    } catch (error) {
      console.error('Error making individual proposition decision:', error);
      toast({
        title: "Erreur",
        description: "Impossible de traiter la décision sur cette proposition",
        variant: "destructive"
      });
    }
  };

  // Gestion des décisions en lot sur toutes les propositions
  const handleBatchPropositionDecisions = async (statut) => {
    try {
      console.log(`Making batch decision: ${statut} for ${pendingPropositions.length} propositions`);
      
      // Préparer les décisions en lot
      const decisions = pendingPropositions.map(prop => ({
        id: prop.id,
        statut: statut,
        decision_remarques: ''
      }));
      
      // Faire les décisions via l'API
      await propositionsService.makeBatchPropositionDecisions(decisions);
      
      // Si validées, intégrer toutes les propositions
      if (statut === 'validé') {
        await integrateSelectedPropositions();
      } else {
        // Si refusées, vider la liste des propositions en attente
        setPendingPropositions([]);
        setShowPropositionsNotification(false);
        
        toast({
          title: "Propositions refusées",
          description: `${pendingPropositions.length} proposition(s) ont été refusées`,
          variant: "default"
        });
      }
      
    } catch (error) {
      console.error('Error making batch proposition decisions:', error);
      toast({
        title: "Erreur",
        description: "Impossible de traiter les décisions en lot",
        variant: "destructive"
      });
    }
  };

  const integratePropositions = async () => {
    try {
      console.log('Checking for active proposition session...');
      const response = await propositionsService.getActiveSession();
      
      if (response.active_session && response.active_session.propositions_count > 0) {
        const sessionToken = response.active_session.session_token;
        console.log('Active session found, loading propositions:', sessionToken);
        
        // Get propositions for this session
        const propositionsData = await propositionsService.listPropositions(sessionToken);
        const propositions = propositionsData.propositions || [];
        
        // Filter: only non-integrated, validated propositions
        const newPropositions = propositions.filter(prop => 
          !prop.integre && !prop.integre_pv && prop.statut === 'validé'
        );
        
        if (newPropositions.length > 0) {
          console.log(`Integrating ${newPropositions.length} new propositions for new PV`);
          
          // Convert propositions to ordre du jour points
          const propositionPoints = newPropositions.map(prop => {
            const currentDate = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
            return {
              titre: prop.titre,
              traite: false,
              notes: prop.description ? [{ date: currentDate, texte: prop.description }] : [],
              source: 'proposition',
              proposition_id: prop.id
            };
          });
          
          // Add to current PV points, avoiding duplicates by title
          setPV(prevPV => {
            const existingTitles = new Set(prevPV.pointsOrdreJour.map(p => p.titre?.trim().toLowerCase()));
            const uniqueNewPoints = propositionPoints.filter(p => 
              !existingTitles.has(p.titre?.trim().toLowerCase())
            );
            
            if (uniqueNewPoints.length === 0) return prevPV;
            
            return {
              ...prevPV,
              pointsOrdreJour: [...prevPV.pointsOrdreJour, ...uniqueNewPoints]
            };
          });
          
          // Show notification
          toast({
            title: "Propositions intégrées",
            description: `${newPropositions.length} proposition(s) validée(s) ajoutée(s) à l'ordre du jour`,
            variant: "default"
          });
          
          return newPropositions.length;
        }
      }
      
      return 0;
    } catch (error) {
      console.error('Error integrating propositions:', error);
      return 0;
    }
  };

  useEffect(() => {
    const initializeForm = async () => {
      setLoading(true);
      
      try {
        // Load members first
        await loadMembers();
        
        // Wait a bit for state to update
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Check if there are saved form data to restore
        const savedFormData = localStorage.getItem('formData');
        const savedMemberPresence = localStorage.getItem('memberPresence');
        
        if (savedFormData && savedMemberPresence) {
          // Restore form data
          setPV(JSON.parse(savedFormData));
          setMemberPresence(JSON.parse(savedMemberPresence));
          localStorage.removeItem('formData');
          localStorage.removeItem('memberPresence');
          setLoading(false);
          return;
        }

        if (id) {
          // Mode modification - load existing PV
          console.log('Mode modification, ID:', id);
          await loadPV(id);
        } else {
          // Mode création - check for duplicated PV or load ongoing tasks
          const duplicated = localStorage.getItem('duplicatedPV');
          if (duplicated) {
            const pvData = JSON.parse(duplicated);
            setPV(pvData);
            localStorage.removeItem('duplicatedPV');
            
            // Initialize member presence for duplicated PV
            const presence = {};
            clubMembers.forEach(member => {
              if (pvData.presents.includes(member)) {
                presence[member] = 'present';
              } else if (pvData.excuses.includes(member)) {
                presence[member] = 'excuse';
              } else {
                presence[member] = 'absent';
              }
            });
            setMemberPresence(presence);
            
            toast({
              title: "PV dupliqué",
              description: "Les données du PV précédent ont été copiées. Vous pouvez les modifier selon vos besoins.",
            });
          } else {
            // Mode nouveau PV - load ongoing tasks and untreated points
            await loadOngoingTasks();
            await loadUntreatedPoints();
            
            // Load and integrate propositions from active session
            await integratePropositions();
            
            // Initialize member presence for new PV
            const presence = {};
            clubMembers.forEach(member => {
              presence[member] = 'absent';
            });
            setMemberPresence(presence);
            
            // Initialize originalPV for change detection in new PV
            setTimeout(() => {
              setPV(currentPV => {
                setOriginalPV({ ...currentPV }); // Save current state as original for comparison
                return currentPV;
              });
            }, 100); // Small delay to ensure all state updates are complete
          }
        }
      } catch (error) {
        console.error('Error initializing form:', error);
        toast({
          title: "Erreur",
          description: "Erreur lors de l'initialisation du formulaire",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    initializeForm();
  }, [id]);

  useEffect(() => {
    // Close status editor when clicking outside
    const handleClickOutside = (event) => {
      if (editingTaskStatus !== null && !event.target.closest('.status-editor')) {
        setEditingTaskStatus(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingTaskStatus]);

  // useEffect pour la sauvegarde automatique toutes les 30s
  useEffect(() => {
    if (loading) return; // Ne pas démarrer l'auto-save pendant le chargement
    
    const autoSaveInterval = setInterval(() => {
      performAutoSave();
    }, 30000); // 30 secondes

    return () => {
      clearInterval(autoSaveInterval);
    };
  }, [pv, memberPresence, saving, loading]);

  // useEffect pour vérifier les nouvelles propositions périodiquement (PV existants uniquement)
  useEffect(() => {
    if (loading || !id) return; // Seulement pour les PV existants et après le chargement
    
    // Vérification initiale
    checkForNewPropositions();
    
    // Vérification périodique toutes les 60 secondes
    const propositionsCheckInterval = setInterval(() => {
      checkForNewPropositions();
    }, 60000); // 1 minute

    return () => {
      clearInterval(propositionsCheckInterval);
    };
  }, [id, loading]);

  // Initialize member presence when clubMembers are loaded and we have a PV
  useEffect(() => {
    if (clubMembers.length > 0 && pv && id) {
      console.log('🔍 DEBUGGING: Checking member presence initialization');
      console.log('clubMembers:', clubMembers);
      console.log('pv.presents:', pv.presents);
      console.log('pv.excuses:', pv.excuses);
      console.log('current memberPresence:', memberPresence);
      
      // Only initialize if we have actual data (not empty arrays from initial load)
      const hasPresentsData = pv.presents && Array.isArray(pv.presents);
      const hasExcusesData = pv.excuses && Array.isArray(pv.excuses);
      
      if (hasPresentsData && hasExcusesData) {
        const presence = {};
        clubMembers.forEach(member => {
          if (pv.presents.includes(member)) {
            presence[member] = 'present';
          } else if (pv.excuses.includes(member)) {
            presence[member] = 'excuse';
          } else {
            presence[member] = 'absent';
          }
        });
        
        console.log('🔍 DEBUGGING: New presence calculated:', presence);
        setMemberPresence(presence);
        console.log('✅ Member presence initialized from saved PV data');
      } else {
        console.log('⏳ Waiting for PV data to be fully loaded...');
      }
    }
  }, [clubMembers, pv?.presents, pv?.excuses, id]); // More specific dependencies

  // Récupération de la sauvegarde automatique au chargement
  useEffect(() => {
    const restoreAutoSave = () => {
      const autoSaveData = localStorage.getItem('autoSave_formData');
      const autoSaveMemberPresence = localStorage.getItem('autoSave_memberPresence');
      const autoSaveTimestamp = localStorage.getItem('autoSave_timestamp');

      if (autoSaveData && autoSaveMemberPresence && autoSaveTimestamp && !id) {
        const saveTime = new Date(autoSaveTimestamp);
        const now = new Date();
        const diffMinutes = (now - saveTime) / (1000 * 60);

        // Si la sauvegarde a moins de 2 heures, proposer de la récupérer
        if (diffMinutes < 120) {
          const shouldRestore = window.confirm(
            `Une sauvegarde automatique de ${saveTime.toLocaleString('fr-FR')} a été trouvée. Voulez-vous la récupérer ?`
          );

          if (shouldRestore) {
            setPV(JSON.parse(autoSaveData));
            setMemberPresence(JSON.parse(autoSaveMemberPresence));
            setLastAutoSave(saveTime);
            toast({
              title: "Sauvegarde restaurée",
              description: "Votre brouillon a été récupéré avec succès.",
            });
          }
        }
        
        // Nettoyer la sauvegarde automatique après utilisation
        localStorage.removeItem('autoSave_formData');
        localStorage.removeItem('autoSave_memberPresence');
        localStorage.removeItem('autoSave_timestamp');
      }
    };

    if (!loading && !id) {
      restoreAutoSave();
    }
  }, [loading, id]);

  const handleMemberPresenceChange = (member, status) => {
    setMemberPresence(prev => ({ ...prev, [member]: status }));
    
    // Mettre à jour les listes présents/excusés immédiatement dans le state PV
    const newPresents = [];
    const newExcuses = [];
    
    clubMembers.forEach(m => {
      const currentStatus = m === member ? status : memberPresence[m];
      if (currentStatus === 'present') {
        newPresents.push(m);
      } else if (currentStatus === 'excuse') {
        newExcuses.push(m);
      }
    });
    
    setPV(prev => ({
      ...prev,
      presents: newPresents,
      excuses: newExcuses
    }));
  };

  // Fonction pour réinitialiser les présences depuis les données sauvegardées
  const resetPresencesToSavedData = () => {
    if (pv && clubMembers.length > 0) {
      const presence = {};
      clubMembers.forEach(member => {
        if (pv.presents && pv.presents.includes(member)) {
          presence[member] = 'present';
        } else if (pv.excuses && pv.excuses.includes(member)) {
          presence[member] = 'excuse';
        } else {
          presence[member] = 'absent';
        }
      });
      setMemberPresence(presence);
      
      toast({
        title: "Présences réinitialisées",
        description: "Les présences ont été restaurées depuis les données sauvegardées.",
      });
    }
  };

  const handlePointChange = (index, field, value) => {
    const newPoints = [...pv.pointsOrdreJour];
    newPoints[index] = { ...newPoints[index], [field]: value };
    setPV(prev => ({ ...prev, pointsOrdreJour: newPoints }));
  };

  const addPoint = () => {
    setPV(prev => ({
      ...prev,
      pointsOrdreJour: [...prev.pointsOrdreJour, { titre: '', traite: false, notes: [] }]
    }));
  };

  const removePoint = (index) => {
    if (pv.pointsOrdreJour.length > 1) {
      const newPoints = pv.pointsOrdreJour.filter((_, i) => i !== index);
      setPV(prev => ({ ...prev, pointsOrdreJour: newPoints }));
    }
  };

  // Configuration des capteurs pour le drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fonction pour gérer la fin du drag & drop
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const activeIndex = parseInt(active.id.replace('point-', ''));
      const overIndex = parseInt(over.id.replace('point-', ''));

      setPV(prev => ({
        ...prev,
        pointsOrdreJour: arrayMove(prev.pointsOrdreJour, activeIndex, overIndex)
      }));
    }
  };

  const toggleNotesVisibility = (index) => {
    setShowNotes(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Add a new dated note to a point
  const addNoteToPoint = (index) => {
    const currentDate = new Date().toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
    
    const newPoints = [...pv.pointsOrdreJour];
    const currentNotes = newPoints[index].notes || [];
    // Add new note at the beginning (most recent first)
    newPoints[index] = {
      ...newPoints[index],
      notes: [{ date: currentDate, texte: '' }, ...currentNotes]
    };
    setPV(prev => ({ ...prev, pointsOrdreJour: newPoints }));
    
    // Open the notes section and set editing mode on the new note
    setShowNotes(prev => ({ ...prev, [index]: true }));
    setEditingNoteIndex({ pointIndex: index, noteIndex: 0 });
  };

  // Update a note's text
  const updateNote = (pointIndex, noteIndex, newTexte) => {
    const newPoints = [...pv.pointsOrdreJour];
    const newNotes = [...(newPoints[pointIndex].notes || [])];
    newNotes[noteIndex] = { ...newNotes[noteIndex], texte: newTexte };
    newPoints[pointIndex] = { ...newPoints[pointIndex], notes: newNotes };
    setPV(prev => ({ ...prev, pointsOrdreJour: newPoints }));
  };

  // Delete a note
  const deleteNote = (pointIndex, noteIndex) => {
    const newPoints = [...pv.pointsOrdreJour];
    const newNotes = (newPoints[pointIndex].notes || []).filter((_, i) => i !== noteIndex);
    newPoints[pointIndex] = { ...newPoints[pointIndex], notes: newNotes };
    setPV(prev => ({ ...prev, pointsOrdreJour: newPoints }));
    setEditingNoteIndex(null);
  };

  // Toggle note termine status
  const toggleNoteTermine = (pointIndex, noteIndex) => {
    const newPoints = [...pv.pointsOrdreJour];
    const newNotes = [...(newPoints[pointIndex].notes || [])];
    newNotes[noteIndex] = { ...newNotes[noteIndex], termine: !newNotes[noteIndex].termine };
    newPoints[pointIndex] = { ...newPoints[pointIndex], notes: newNotes };
    setPV(prev => ({ ...prev, pointsOrdreJour: newPoints }));
  };


  // Auto-approve last PV
  const approveLastPV = () => {
    const approvalText = "Le procès-verbal est approuvé sans remarque.";
    setPV(prev => ({ ...prev, approbationDernierPV: approvalText }));
    
    // Focus the textarea after setting text
    setTimeout(() => {
      const textarea = document.querySelector('textarea[placeholder*="Statut de l\'approbation"]');
      if (textarea) {
        textarea.focus();
        // Position cursor at the end
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      }
    }, 10);
  };

  // Check if PV has been modified
  const hasUnsavedChanges = () => {
    if (!originalPV) return false;
    
    // Deep comparison of key fields that indicate changes
    const currentForComparison = {
      date: pv.date,
      time: pv.time,
      presents: pv.presents.sort(),
      excuses: pv.excuses.sort(),
      approbationDernierPV: pv.approbationDernierPV,
      suiviDecisions: pv.suiviDecisions,
      pointsOrdreJour: pv.pointsOrdreJour.filter(p => p.titre?.trim()),
      diversPropositions: pv.diversPropositions,
      propositionsIndividuelles: pv.propositionsIndividuelles || [],
      prochainEtapes: pv.prochainEtapes.filter(e => e.tache?.trim()),
      prochainComite: pv.prochainComite,
      taskSort: taskSort,
      taskSortDirection: taskSortDirection
    };
    
    const originalForComparison = {
      date: originalPV.date,
      time: originalPV.time,
      presents: originalPV.presents.sort(),
      excuses: originalPV.excuses.sort(),
      approbationDernierPV: originalPV.approbationDernierPV,
      suiviDecisions: originalPV.suiviDecisions,
      pointsOrdreJour: originalPV.pointsOrdreJour.filter(p => p.titre?.trim()),
      diversPropositions: originalPV.diversPropositions,
      propositionsIndividuelles: originalPV.propositionsIndividuelles || [],
      prochainEtapes: originalPV.prochainEtapes.filter(e => e.tache?.trim()),
      prochainComite: originalPV.prochainComite,
      taskSort: originalPV.taskSort || 'statut',
      taskSortDirection: originalPV.taskSortDirection || 'asc'
    };
    
    return JSON.stringify(currentForComparison) !== JSON.stringify(originalForComparison);
  };

  // Handle back navigation with confirmation if needed
  const handleBackNavigation = () => {
    if (hasUnsavedChanges()) {
      setShowExitConfirmation(true);
    } else {
      // If editing existing PV, return to preview; if new PV, go to dashboard
      if (id) {
        navigate(`/pv/${id}`);
      } else {
        navigate('/dashboard');
      }
    }
  };

  // Confirm exit without saving
  const confirmExitWithoutSaving = () => {
    setShowExitConfirmation(false);
    // If editing existing PV, return to preview; if new PV, go to dashboard
    if (id) {
      navigate(`/pv/${id}`);
    } else {
      navigate('/dashboard');
    }
  };

  // Cancel exit and stay in form
  const cancelExit = () => {
    setShowExitConfirmation(false);
  };

  const handleEtapeChange = (index, field, value) => {
    const newEtapes = [...pv.prochainEtapes];
    newEtapes[index] = { ...newEtapes[index], [field]: value };
    setPV(prev => ({ ...prev, prochainEtapes: newEtapes }));
  };

  const addEtape = () => {
    setPV(prev => ({
      ...prev,
      prochainEtapes: [...prev.prochainEtapes, { tache: '', responsable: '', delai: '', statut: 'En cours' }]
    }));
  };

  const removeEtape = (index) => {
    if (pv.prochainEtapes.length > 1) {
      const newEtapes = pv.prochainEtapes.filter((_, i) => i !== index);
      setPV(prev => ({ ...prev, prochainEtapes: newEtapes }));
    }
  };

  // Helper function to get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      'En cours': { 
        color: 'bg-yellow-100 text-yellow-700 border-yellow-200', 
        label: 'En cours' 
      },
      'Terminé': { 
        color: 'bg-green-100 text-green-700 border-green-200', 
        label: 'Terminé' 
      },
      'En suspens': { 
        color: 'bg-blue-100 text-blue-700 border-blue-200', 
        label: 'En suspens' 
      }
    };
    
    const config = statusConfig[status] || statusConfig['En cours'];
    return config;
  };

  // Handle status editing
  const handleStatusEdit = (taskIndex) => {
    setEditingTaskStatus(taskIndex);
  };

  const handleStatusChange = (taskIndex, newStatus) => {
    handleEtapeChange(taskIndex, 'statut', newStatus);
    setEditingTaskStatus(null);
  };

  // Handle propositions individuelles 
  const addProposition = () => {
    if (nouveauProposition.membre && nouveauProposition.contenu.trim()) {
      setPV(prev => ({
        ...prev,
        propositionsIndividuelles: [
          ...(prev.propositionsIndividuelles || []),
          {
            membre: nouveauProposition.membre,
            contenu: nouveauProposition.contenu.trim(),
            statut: null, // En attente de décision
            remarques: ''
          }
        ]
      }));
      
      // Reset form
      setNouveauProposition({ membre: '', contenu: '' });
      
      toast({
        title: "Proposition ajoutée",
        description: `Proposition de ${nouveauProposition.membre} ajoutée avec succès.`,
      });
    }
  };

  const removeProposition = (index) => {
    setPV(prev => ({
      ...prev,
      propositionsIndividuelles: prev.propositionsIndividuelles.filter((_, i) => i !== index)
    }));
    
    toast({
      title: "Proposition supprimée",
      description: "La proposition a été supprimée.",
    });
  };

  const editProposition = (index) => {
    const proposition = pv.propositionsIndividuelles[index];
    setNouveauProposition({
      membre: proposition.membre,
      contenu: proposition.contenu
    });
    setEditingProposition(index);
  };

  const updateProposition = () => {
    if (editingProposition !== null && nouveauProposition.membre && nouveauProposition.contenu.trim()) {
      setPV(prev => ({
        ...prev,
        propositionsIndividuelles: prev.propositionsIndividuelles.map((prop, index) => 
          index === editingProposition ? {
            membre: nouveauProposition.membre,
            contenu: nouveauProposition.contenu.trim()
          } : prop
        )
      }));
      
      // Reset form
      setNouveauProposition({ membre: '', contenu: '' });
      setEditingProposition(null);
      
      toast({
        title: "Proposition modifiée",
        description: `Proposition de ${nouveauProposition.membre} mise à jour.`,
      });
    }
  };

  const cancelEditProposition = () => {
    setNouveauProposition({ membre: '', contenu: '' });
    setEditingProposition(null);
  };

  // Fonction pour trier les tâches uniquement
  const getSortedTasks = () => {
    let tasks = [...pv.prochainEtapes];

    // Tri
    tasks.sort((a, b) => {
      let comparison = 0;
      
      switch (taskSort) {
        case 'statut':
          const statusOrder = { 'En cours': 1, 'En suspens': 2, 'Terminé': 3 };
          comparison = (statusOrder[a.statut] || 0) - (statusOrder[b.statut] || 0);
          break;
        case 'responsable':
          comparison = (a.responsable || '').localeCompare(b.responsable || '');
          break;
        case 'delai':
          if (!a.delai && !b.delai) comparison = 0;
          else if (!a.delai) comparison = 1;
          else if (!b.delai) comparison = -1;
          else comparison = new Date(a.delai) - new Date(b.delai);
          break;
        case 'tache':
          comparison = (a.tache || '').localeCompare(b.tache || '');
          break;
        default:
          comparison = 0;
      }
      
      return taskSortDirection === 'desc' ? -comparison : comparison;
    });

    return tasks;
  };

  // Fonction pour changer le tri
  const handleSort = (column) => {
    let newDirection = 'asc';
    
    if (taskSort === column) {
      // Si on clique sur la même colonne, inverser la direction
      newDirection = taskSortDirection === 'asc' ? 'desc' : 'asc';
      setTaskSortDirection(newDirection);
    } else {
      // Si nouvelle colonne, commencer par ascendant
      setTaskSort(column);
      setTaskSortDirection(newDirection);
    }

    // Mettre à jour le PV avec les nouvelles préférences de tri
    setPV(prev => ({
      ...prev,
      taskSort: column,
      taskSortDirection: newDirection
    }))

    // Sauvegarde immédiate pour les PV existants
    if (id && !saving) {
      saveSortPreferences(column, newDirection);
    }
  };

  // Fonction pour sauvegarder les préférences de tri immédiatement
  const saveSortPreferences = async (newTaskSort, newTaskSortDirection) => {
    try {
      console.log('Saving sort preferences:', { newTaskSort, newTaskSortDirection });
      setSaving(true);
      
      // Prepare PV data with new sort preferences
      const pvToSave = {
        ...pv,
        taskSort: newTaskSort,
        taskSortDirection: newTaskSortDirection,
        presents: Object.keys(memberPresence).filter(member => memberPresence[member] === 'present'),
        excuses: Object.keys(memberPresence).filter(member => memberPresence[member] === 'excuse'),
        pointsOrdreJour: pv.pointsOrdreJour.filter(p => p.titre?.trim()).map(p => ({
          ...p,
          remarques: (p.notes || []).map(n => `[${n.date}] ${n.texte}`).join('\n')
        })),
        prochainEtapes: pv.prochainEtapes.filter(e => e.tache?.trim())
      };

      if (id) {
        await pvService.update(id, pvToSave);
        console.log('Sort preferences saved successfully');
        setOriginalPV({ ...pvToSave });
      }
      
    } catch (error) {
      console.error('Error saving sort preferences:', error);
      // Silent fail - don't disturb user for sort preferences
    } finally {
      setSaving(false);
    }
  };

  // Sauvegarde
  const handleSave = async () => {
    if (saving) return; // Prevent double save
    try {
      setSaving(true);
      
      // Get lists of present and excused members
      const presents = Object.keys(memberPresence).filter(member => memberPresence[member] === 'present');
      const excuses = Object.keys(memberPresence).filter(member => memberPresence[member] === 'excuse');
      
      console.log('🔍 DEBUGGING SAVE:');
      console.log('memberPresence state:', memberPresence);
      console.log('calculated presents:', presents);
      console.log('calculated excuses:', excuses);
      
      // Clean up empty points and tasks
      const cleanedPoints = pv.pointsOrdreJour.filter(p => p.titre?.trim()).map(p => ({
        ...p,
        // Generate remarques from notes for backward compatibility (PDF, preview)
        remarques: (p.notes || []).map(n => `[${n.date}] ${n.texte}`).join('\n')
      }));
      const cleanedTasks = pv.prochainEtapes.filter(e => e.tache?.trim());
      
      // Prepare PV data for API
      const pvToSave = {
        ...pv,
        presents,
        excuses,
        pointsOrdreJour: cleanedPoints,
        prochainEtapes: cleanedTasks,
        taskSort: taskSort,
        taskSortDirection: taskSortDirection
      };

      console.log('🔍 DEBUGGING: Data being sent to API:', {
        presents: pvToSave.presents,
        excuses: pvToSave.excuses,
        id: id
      });

      let savedPV;
      if (id) {
        // Update existing PV
        savedPV = await pvService.update(id, pvToSave);
        
        // Mark propositions as integrated if there are any
        const propositionIds = cleanedPoints
          .filter(point => point.proposition_id)
          .map(point => point.proposition_id);
          
        if (propositionIds.length > 0) {
          try {
            await propositionsService.markPropositionsAsIntegrated(propositionIds);
            console.log(`Marked ${propositionIds.length} propositions as integrated`);
          } catch (error) {
            console.error('Error marking propositions as integrated:', error);
            // Don't fail the save for this
          }
        }
        
        toast({
          title: "PV modifié",
          description: `PV du ${new Date(pv.date).toLocaleDateString('fr-FR')} mis à jour avec succès`,
        });
      } else {
        // Create new PV
        savedPV = await pvService.create(pvToSave);
        
        // Mark propositions as integrated if there are any
        const propositionIds = cleanedPoints
          .filter(point => point.proposition_id)
          .map(point => point.proposition_id);
          
        if (propositionIds.length > 0) {
          try {
            await propositionsService.markPropositionsAsIntegrated(propositionIds);
            console.log(`Marked ${propositionIds.length} propositions as integrated for club ${savedPV.id}`);
          } catch (error) {
            console.error('Error marking propositions as integrated:', error);
            // Don't fail the save for this
          }
        }
        
        toast({
          title: "PV créé",
          description: `Nouveau PV du ${new Date(pv.date).toLocaleDateString('fr-FR')} créé avec succès`,
        });
        
        // Navigate to edit mode to prevent double creation on next save
        if (savedPV?.id) {
          navigate(`/pv/${savedPV.id}/edit`, { replace: true });
        }
      }
      
      // Update original PV state for change detection
      setOriginalPV({ ...pvToSave });
      
      // Also update current PV state to reflect saved presents/excuses
      setPV(prev => ({
        ...prev,
        presents,
        excuses
      }));
      
      // Clear auto-save data on successful save
      localStorage.removeItem('autoSave_formData');
      localStorage.removeItem('autoSave_memberPresence');
      localStorage.removeItem('autoSave_timestamp');
      setLastAutoSave(null);
      setAutoSaveStatus('saved');
      
      setTimeout(() => {
        setAutoSaveStatus('idle');
      }, 3000);

      return savedPV;
    } catch (error) {
      console.error('Error saving PV:', error);
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur de sauvegarde",
        description: `Impossible de sauvegarder le PV: ${errorMessage}`,
        variant: "destructive"
      });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // Sauvegarde automatique
  const performAutoSave = async () => {
    if (saving || !hasUnsavedChanges()) return;
    
    try {
      setAutoSaveStatus('saving');
      
      // Save in localStorage for now (could be enhanced with API auto-save)
      const autoSaveData = {
        ...pv,
        taskSort: taskSort,
        taskSortDirection: taskSortDirection
      };
      
      localStorage.setItem('autoSave_formData', JSON.stringify(autoSaveData));
      localStorage.setItem('autoSave_memberPresence', JSON.stringify(memberPresence));
      localStorage.setItem('autoSave_timestamp', new Date().toISOString());
      
      const now = new Date();
      setLastAutoSave(now);
      setAutoSaveStatus('saved');
      
      setTimeout(() => {
        setAutoSaveStatus('idle');
      }, 2000);
      
    } catch (error) {
      console.error('Auto-save error:', error);
      setAutoSaveStatus('error');
      setTimeout(() => {
        setAutoSaveStatus('idle');
      }, 3000);
    }
  };

  const handlePreview = async () => {
    try {
      const savedPV = await handleSave();
      // Navigate to preview with the saved PV ID
      const pvId = savedPV?.id || id;
      if (pvId) {
        navigate(`/pv/${pvId}`);
      }
    } catch (error) {
      // Error already handled in handleSave
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center p-12">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-green-600 mx-auto mb-3" />
              <p className="text-gray-600">Chargement des PVs...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-50 p-2 sm:p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header Navigation - Mobile Optimized */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBackNavigation}
              className="border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Retour</span>
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900">
                {id ? 'Modifier le PV' : 'Nouveau PV'}
              </h1>
              {!id && (
                <div className="flex items-center text-xs sm:text-sm text-gray-500 mt-1">
                  <span className="inline-flex items-center">
                    Test Auto-Save
                    {autoSaveStatus === 'saving' && (
                      <Loader2 className="w-3 h-3 animate-spin ml-2 text-blue-500" />
                    )}
                    {autoSaveStatus === 'saved' && (
                      <span className="ml-2 text-green-600">✓</span>
                    )}
                    {autoSaveStatus === 'error' && (
                      <span className="ml-2 text-red-600">✗</span>
                    )}
                  </span>
                  {lastAutoSave && (
                    <span className="ml-2 text-gray-400">
                      {lastAutoSave.toLocaleTimeString('fr-FR', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={saving}
              className="border-green-200 text-green-600 hover:bg-green-50 text-sm"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Sauvegarde...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Sauvegarder</span>
                </>
              )}
            </Button>
            <Button
              onClick={handlePreview}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white text-sm"
            >
              <Eye className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Aperçu</span>
            </Button>
          </div>
        </div>

        {/* Exit Confirmation Modal */}
        {showExitConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full mx-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Modifications non sauvegardées
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Vous avez des modifications non sauvegardées. Que souhaitez-vous faire ?
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      onClick={async () => {
                        try {
                          await handleSave();
                          setShowExitConfirmation(false);
                          if (id) {
                            navigate(`/pv/${id}`);
                          } else {
                            navigate('/dashboard');
                          }
                        } catch (error) {
                          // Error already handled in handleSave
                        }
                      }}
                      disabled={saving}
                      className="bg-green-600 hover:bg-green-700 text-white text-sm"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Sauvegarde...
                        </>
                      ) : (
                        'Sauvegarder et quitter'
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={confirmExitWithoutSaving}
                      className="border-red-200 text-red-600 hover:bg-red-50 text-sm"
                    >
                      Quitter sans sauvegarder
                    </Button>
                    <Button
                      variant="outline"
                      onClick={cancelExit}
                      className="text-sm"
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notification des nouvelles propositions - Mobile Optimized */}
        {showPropositionsNotification && pendingPropositions.length > 0 && (
          <div className="mb-4 sm:mb-6">
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col gap-3">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-blue-900 text-sm sm:text-base">
                        {pendingPropositions.length} nouvelle(s) proposition(s) disponible(s)
                      </h4>
                      <p className="text-xs text-blue-700 mt-1">
                        Validez ou refusez individuellement chaque proposition
                      </p>
                    </div>
                  </div>
                  
                  {/* Liste des propositions avec options validation/refus */}
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {pendingPropositions.map((prop) => (
                      <div key={prop.id} className="bg-white p-3 rounded-lg border border-blue-200">
                        <div className="space-y-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm text-blue-900">{prop.membre}</span>
                              <span className="text-xs text-blue-700">propose :</span>
                            </div>
                            <p className="text-sm text-gray-700 font-medium">{prop.titre}</p>
                            {prop.description && (
                              <p className="text-xs text-gray-600 mt-1">{prop.description}</p>
                            )}
                          </div>
                          
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleIndividualPropositionDecision(prop.id, 'validé')}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1 h-auto flex-1 sm:flex-none"
                            >
                              ✓ Valider
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleIndividualPropositionDecision(prop.id, 'refusé')}
                              className="border-red-300 text-red-700 hover:bg-red-50 text-xs px-3 py-1 h-auto flex-1 sm:flex-none"
                            >
                              ✗ Refuser
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-blue-200">
                    <Button
                      size="sm"
                      onClick={() => handleBatchPropositionDecisions('validé')}
                      className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-2 h-auto"
                    >
                      ✓ Tout valider ({pendingPropositions.length})
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleBatchPropositionDecisions('refusé')}
                      className="border-red-300 text-red-700 hover:bg-red-50 text-xs px-3 py-2 h-auto"
                    >
                      ✗ Tout refuser ({pendingPropositions.length})
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowPropositionsNotification(false)}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100 text-xs px-3 py-2 h-auto"
                    >
                      Plus tard
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="space-y-4 sm:space-y-6">
          {/* Informations générales - Mobile Optimized */}
          <Card className="border-green-200 bg-white shadow-md">
            <CardHeader className="pb-3 sm:pb-4">
              <CardTitle className="flex items-center text-green-700 text-base sm:text-lg">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Informations générales
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date" className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4" />
                    Date
                  </Label>
                  <Input
                    id="date"
                    type="date"
                    value={pv.date}
                    onChange={(e) => setPV(prev => ({ ...prev, date: e.target.value }))}
                    className="border-green-200 focus:border-green-400 cursor-pointer text-sm sm:text-base"
                  />
                </div>
                <div>
                  <Label htmlFor="time" className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4" />
                    Heure
                  </Label>
                  <select
                    id="time"
                    value={pv.time}
                    onChange={(e) => setPV(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full px-3 py-2 border border-green-200 rounded-md focus:border-green-400 focus:outline-none text-sm sm:text-base"
                  >
                    <option value="17:00">17:00</option>
                    <option value="17:15">17:15</option>
                    <option value="17:30">17:30</option>
                    <option value="17:45">17:45</option>
                    <option value="18:00">18:00</option>
                    <option value="18:15">18:15</option>
                    <option value="18:30">18:30</option>
                    <option value="18:45">18:45</option>
                    <option value="19:00">19:00</option>
                    <option value="19:15">19:15</option>
                    <option value="19:30">19:30</option>
                    <option value="19:45">19:45</option>
                    <option value="20:00">20:00</option>
                    <option value="20:15">20:15</option>
                    <option value="20:30">20:30</option>
                    <option value="20:45">20:45</option>
                    <option value="21:00">21:00</option>
                    <option value="21:15">21:15</option>
                    <option value="21:30">21:30</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Présents et Excusés - Compact Design */}
          <Card className="border-green-200 bg-white shadow-md">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center text-green-700 text-base sm:text-lg">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Présences
                </CardTitle>
                {id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetPresencesToSavedData}
                    className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 h-auto"
                    title="Restaurer les présences sauvegardées"
                  >
                    🔄 Restaurer
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {clubMembers.map((member) => (
                  <div key={member} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                    <Label className="text-sm font-medium text-gray-800 min-w-0 flex-shrink-0 mr-3">
                      {member}
                    </Label>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleMemberPresenceChange(member, 'present')}
                        className={`px-2 py-1 rounded-md text-xs font-medium transition-colors min-w-[60px] ${
                          memberPresence[member] === 'present'
                            ? 'bg-green-600 text-white'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        Présent
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMemberPresenceChange(member, 'excuse')}
                        className={`px-2 py-1 rounded-md text-xs font-medium transition-colors min-w-[55px] ${
                          memberPresence[member] === 'excuse'
                            ? 'bg-orange-600 text-white'
                            : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                        }`}
                      >
                        Excusé
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMemberPresenceChange(member, 'absent')}
                        className={`px-2 py-1 rounded-md text-xs font-medium transition-colors min-w-[50px] ${
                          memberPresence[member] === 'absent'
                            ? 'bg-gray-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Absent
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Approbation du dernier PV - Mobile Optimized */}
          <Card className="border-green-200 bg-white shadow-md">
            <CardHeader className="pb-3 sm:pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <CardTitle className="flex items-center text-green-700 text-base sm:text-lg">
                  <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  1. Approbation du dernier PV
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={approveLastPV}
                  className="border-green-200 text-green-600 hover:bg-green-50 text-xs px-3 py-1 h-auto w-fit"
                  title="Approuver sans remarque"
                >
                  ✅ Approuver
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Textarea
                value={pv.approbationDernierPV}
                onChange={(e) => setPV(prev => ({ ...prev, approbationDernierPV: e.target.value }))}
                placeholder="Statut de l'approbation du PV précédent..."
                className="border-green-200 focus:border-green-400 text-sm"
                rows={3}
              />
            </CardContent>
          </Card>

          {/* Suivi des décisions précédentes - Mobile Optimized */}
          <Card className="border-green-200 bg-white shadow-md">
            <CardHeader className="pb-3 sm:pb-4">
              <CardTitle className="flex items-center text-green-700 text-base sm:text-lg">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                2. Suivi des décisions précédentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={pv.suiviDecisions}
                onChange={(e) => setPV(prev => ({ ...prev, suiviDecisions: e.target.value }))}
                placeholder="État d'avancement des décisions du comité précédent..."
                className="border-green-200 focus:border-green-400 text-sm"
                rows={3}
              />
            </CardContent>
          </Card>

          {/* Points à l'ordre du jour - Mobile Optimized */}
          <Card className="border-green-200 bg-white shadow-md">
            <CardHeader className="pb-3 sm:pb-4">
              <CardTitle className="flex items-center text-green-700 text-base sm:text-lg">
                <ListTodo className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                3. Points à l'ordre du jour
              </CardTitle>
              <p className="text-xs sm:text-sm text-gray-600 mt-2 flex items-center">
                <GripVertical className="w-4 h-4 mr-1" />
                Glissez-déposez les points pour les réorganiser
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <DndContext 
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext 
                  items={pv.pointsOrdreJour.map((_, index) => `point-${index}`)}
                  strategy={verticalListSortingStrategy}
                >
                  {pv.pointsOrdreJour.map((point, index) => (
                    <SortablePointItem
                      key={`point-${index}`}
                      point={point}
                      index={index}
                      totalPoints={pv.pointsOrdreJour.length}
                      showNotes={showNotes}
                      onPointChange={handlePointChange}
                      onRemovePoint={removePoint}
                      onToggleNotes={toggleNotesVisibility}
                      onAddNote={addNoteToPoint}
                      onUpdateNote={updateNote}
                      onDeleteNote={deleteNote}
                      onToggleNoteTermine={toggleNoteTermine}
                      editingNoteIndex={editingNoteIndex}
                      setEditingNoteIndex={setEditingNoteIndex}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <Button
                variant="outline"
                onClick={addPoint}
                className="border-indigo-200 text-indigo-600 hover:bg-indigo-50 w-full sm:w-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un point
              </Button>
            </CardContent>
          </Card>

          {/* Divers / Propositions individuelles - Mobile Optimized */}
          <Card className="border-green-200 bg-white shadow-md">
            <CardHeader className="pb-3 sm:pb-4">
              <CardTitle className="flex items-center text-green-700 text-base sm:text-lg">
                4. Divers / Propositions individuelles
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Propositions structurées */}
              {pv.propositionsIndividuelles && pv.propositionsIndividuelles.length > 0 && (
                <div className="space-y-3">
                  {pv.propositionsIndividuelles.map((proposition, index) => (
                    <div key={index} className={`p-3 sm:p-4 rounded-lg border-2 ${
                      editingProposition === index ? 'border-yellow-300 bg-yellow-50' : 
                      proposition.statut === 'validé' ? 'border-green-300 bg-green-50' :
                      proposition.statut === 'refusé' ? 'border-red-300 bg-red-50' :
                      'border-gray-200 bg-gray-50'
                    }`}>
                      <div className="flex flex-col gap-3">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="text-sm font-semibold text-gray-700">
                              {proposition.membre}
                            </span>
                            <span className="text-sm text-gray-600">propose :</span>
                            
                            {/* Badge de statut */}
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              proposition.statut === 'validé' ? 'bg-green-200 text-green-800' :
                              proposition.statut === 'refusé' ? 'bg-red-200 text-red-800' :
                              'bg-gray-200 text-gray-800'
                            }`}>
                              {proposition.statut === 'validé' ? '✅ Validé' :
                               proposition.statut === 'refusé' ? '❌ Refusé' :
                               '⏳ En discussion'}
                            </span>
                            
                            {editingProposition === index && (
                              <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full">
                                En cours d'édition
                              </span>
                            )}
                          </div>
                          <p className="text-sm sm:text-base text-gray-700">{proposition.contenu}</p>
                          
                          {/* Remarques sur la décision */}
                          {proposition.remarques && (
                            <div className="mt-2 p-2 bg-gray-100 rounded text-sm">
                              <span className="font-medium text-gray-700">Remarques :</span>
                              <span className="text-gray-600 ml-1">{proposition.remarques}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          {/* Boutons de décision (seulement si pas encore décidé) */}
                          {!proposition.statut && (
                            <div className="flex gap-2 flex-wrap">
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => {
                                  const updatedPropositions = [...pv.propositionsIndividuelles];
                                  updatedPropositions[index] = { ...proposition, statut: 'validé' };
                                  setPV(prev => ({ ...prev, propositionsIndividuelles: updatedPropositions }));
                                }}
                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-xs flex-shrink-0"
                                title="Valider cette proposition"
                              >
                                ✅ Valider
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => {
                                  const remarques = prompt("Remarques sur le refus (optionnel) :");
                                  const updatedPropositions = [...pv.propositionsIndividuelles];
                                  updatedPropositions[index] = { 
                                    ...proposition, 
                                    statut: 'refusé',
                                    remarques: remarques || ''
                                  };
                                  setPV(prev => ({ ...prev, propositionsIndividuelles: updatedPropositions }));
                                }}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 text-xs flex-shrink-0"
                                title="Refuser cette proposition"
                              >
                                ❌ Refuser
                              </Button>
                            </div>
                          )}
                          
                          {/* Boutons d'édition/suppression */}
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => editProposition(index)}
                              disabled={editingProposition !== null}
                              className="h-8 px-2 text-gray-500 hover:text-gray-700 hover:bg-gray-50 text-xs"
                              title="Modifier cette proposition"
                            >
                              <Edit3 className="w-4 h-4 mr-1" />
                              Modifier
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeProposition(index)}
                              disabled={editingProposition !== null}
                              className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50 text-xs"
                              title="Supprimer cette proposition"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Supprimer
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Formulaire d'ajout/édition de proposition - Mobile Optimized */}
              <div className="space-y-3 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <h4 className="font-medium text-gray-700 text-sm sm:text-base">
                    {editingProposition !== null ? 'Modifier la proposition' : 'Ajouter une proposition'}
                  </h4>
                  {editingProposition !== null && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={cancelEditProposition}
                      className="text-gray-500 hover:text-gray-700 text-xs w-fit"
                    >
                      Annuler
                    </Button>
                  )}
                </div>
                
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="nouveau-proposition-membre" className="text-sm">Membre</Label>
                    <select
                      id="nouveau-proposition-membre"
                      value={nouveauProposition.membre}
                      onChange={(e) => setNouveauProposition(prev => ({ ...prev, membre: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                    >
                      <option value="">Sélectionner un membre...</option>
                      {clubMembers.map(member => (
                        <option key={member} value={member}>{member}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <Label htmlFor="nouveau-proposition-contenu" className="text-sm">Proposition</Label>
                    <Textarea
                      id="nouveau-proposition-contenu"
                      value={nouveauProposition.contenu}
                      onChange={(e) => setNouveauProposition(prev => ({ ...prev, contenu: e.target.value }))}
                      placeholder="Détail de la proposition..."
                      className="border-gray-300 focus:border-green-400 text-sm"
                      rows={3}
                    />
                  </div>
                  
                  <Button
                    type="button"
                    onClick={editingProposition !== null ? updateProposition : addProposition}
                    disabled={!nouveauProposition.membre || !nouveauProposition.contenu.trim()}
                    className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {editingProposition !== null ? 'Mettre à jour' : 'Ajouter la proposition'}
                  </Button>
                </div>
              </div>

              {/* Section divers traditionnelle */}
              <div>
                <Label htmlFor="divers" className="text-sm font-medium text-gray-700 mb-2 block">
                  Divers (format libre)
                </Label>
                <Textarea
                  id="divers"
                  value={pv.diversPropositions}
                  onChange={(e) => setPV(prev => ({ ...prev, diversPropositions: e.target.value }))}
                  placeholder="Points divers, questions des membres, etc."
                  className="border-green-200 focus:border-green-400 text-sm"
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Prochaines étapes et tâches attribuées - Mobile Optimized */}
          <Card className="border-green-200 bg-white shadow-md">
            <CardHeader className="pb-3 sm:pb-4">
              <CardTitle className="flex items-center text-green-700 text-base sm:text-lg">
                <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                5. Prochaines étapes et tâches attribuées
              </CardTitle>
              <p className="text-xs sm:text-sm text-gray-600 mt-2">
                Cliquez sur les en-têtes de colonnes pour trier
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Mobile-First Task Display */}
              {pv.prochainEtapes && pv.prochainEtapes.length > 0 && (
                <>
                  {/* Mobile Card Layout (visible on small screens) */}
                  <div className="sm:hidden space-y-3">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Trier par :</span>
                      <select
                        value={taskSort}
                        onChange={(e) => handleSort(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-xs focus:border-green-400 focus:outline-none"
                      >
                        <option value="statut">Statut</option>
                        <option value="responsable">Responsable</option>
                        <option value="delai">Délai</option>
                        <option value="tache">Tâche</option>
                      </select>
                    </div>
                    
                    {getSortedTasks().map((etape, index) => {
                      const originalIndex = pv.prochainEtapes.findIndex(e => e === etape);
                      const statusConfig = getStatusBadge(etape.statut);
                      
                      return (
                        <div key={originalIndex} className="border border-gray-200 rounded-lg p-3 space-y-3 bg-white">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 font-medium">Tâche #{index + 1}</span>
                            {pv.prochainEtapes.length > 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeEtape(originalIndex)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                title="Supprimer cette tâche"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          
                          <div className="space-y-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Tâche</label>
                              <Input
                                value={etape.tache}
                                onChange={(e) => handleEtapeChange(originalIndex, 'tache', e.target.value)}
                                placeholder="Décrire la tâche..."
                                className="border-green-200 focus:border-green-400 text-sm"
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Responsable</label>
                                <select
                                  value={etape.responsable}
                                  onChange={(e) => handleEtapeChange(originalIndex, 'responsable', e.target.value)}
                                  className="w-full px-2 py-1 border border-green-200 rounded focus:border-green-400 focus:outline-none text-sm"
                                >
                                  <option value="">Choisir...</option>
                                  {clubMembers.map(member => (
                                    <option key={member} value={member}>{member}</option>
                                  ))}
                                </select>
                              </div>
                              
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Délai</label>
                                <Input
                                  type="date"
                                  value={etape.delai}
                                  onChange={(e) => handleEtapeChange(originalIndex, 'delai', e.target.value)}
                                  className="border-green-200 focus:border-green-400 text-sm"
                                />
                              </div>
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Statut</label>
                              {editingTaskStatus === originalIndex ? (
                                <div className="status-editor">
                                  <select
                                    value={etape.statut}
                                    onChange={(e) => handleStatusChange(originalIndex, e.target.value)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded focus:border-green-400 focus:outline-none text-sm"
                                    autoFocus
                                  >
                                    <option value="En cours">En cours</option>
                                    <option value="Terminé">Terminé</option>
                                    <option value="En suspens">En suspens</option>
                                  </select>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleStatusEdit(originalIndex)}
                                  className={`px-3 py-2 rounded-lg text-sm font-medium border cursor-pointer hover:opacity-80 w-full text-center ${statusConfig.color}`}
                                >
                                  {statusConfig.label}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Desktop Table Layout (visible on larger screens) */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th 
                            className="text-left p-2 text-xs sm:text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50 min-w-[120px]"
                            onClick={() => handleSort('tache')}
                          >
                            Tâche
                            {taskSort === 'tache' && (
                              <span className="ml-1">
                                {taskSortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                          <th 
                            className="text-left p-2 text-xs sm:text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50 min-w-[100px]"
                            onClick={() => handleSort('responsable')}
                          >
                            Responsable
                            {taskSort === 'responsable' && (
                              <span className="ml-1">
                                {taskSortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                          <th 
                            className="text-left p-2 text-xs sm:text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50 min-w-[90px]"
                            onClick={() => handleSort('delai')}
                          >
                            Délai
                            {taskSort === 'delai' && (
                              <span className="ml-1">
                                {taskSortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                          <th 
                            className="text-left p-2 text-xs sm:text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50 min-w-[80px]"
                            onClick={() => handleSort('statut')}
                          >
                            Statut
                            {taskSort === 'statut' && (
                              <span className="ml-1">
                                {taskSortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                          <th className="text-left p-2 text-xs sm:text-sm font-medium text-gray-700 min-w-[60px]">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {getSortedTasks().map((etape, index) => {
                          const originalIndex = pv.prochainEtapes.findIndex(e => e === etape);
                          const statusConfig = getStatusBadge(etape.statut);
                          
                          return (
                            <tr key={originalIndex} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="p-2">
                                <Input
                                  value={etape.tache}
                                  onChange={(e) => handleEtapeChange(originalIndex, 'tache', e.target.value)}
                                  placeholder="Décrire la tâche..."
                                  className="border-green-200 focus:border-green-400 text-xs sm:text-sm"
                                />
                              </td>
                              <td className="p-2">
                                <select
                                  value={etape.responsable}
                                  onChange={(e) => handleEtapeChange(originalIndex, 'responsable', e.target.value)}
                                  className="w-full px-2 py-1 border border-green-200 rounded focus:border-green-400 focus:outline-none text-xs sm:text-sm"
                                >
                                  <option value="">Sélectionner...</option>
                                  {clubMembers.map(member => (
                                    <option key={member} value={member}>{member}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="p-2">
                                <Input
                                  type="date"
                                  value={etape.delai}
                                  onChange={(e) => handleEtapeChange(originalIndex, 'delai', e.target.value)}
                                  className="border-green-200 focus:border-green-400 text-xs sm:text-sm"
                                />
                              </td>
                              <td className="p-2">
                                {editingTaskStatus === originalIndex ? (
                                  <div className="status-editor">
                                    <select
                                      value={etape.statut}
                                      onChange={(e) => handleStatusChange(originalIndex, e.target.value)}
                                      className="w-full px-2 py-1 border border-gray-300 rounded focus:border-green-400 focus:outline-none text-xs sm:text-sm"
                                      autoFocus
                                    >
                                      <option value="En cours">En cours</option>
                                      <option value="Terminé">Terminé</option>
                                      <option value="En suspens">En suspens</option>
                                    </select>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => handleStatusEdit(originalIndex)}
                                    className={`px-2 py-1 rounded-full text-xs font-medium border cursor-pointer hover:opacity-80 w-full text-center ${statusConfig.color}`}
                                  >
                                    {statusConfig.label}
                                  </button>
                                )}
                              </td>
                              <td className="p-2">
                                {pv.prochainEtapes.length > 1 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeEtape(originalIndex)}
                                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    title="Supprimer cette tâche"
                                  >
                                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              
              <Button
                variant="outline"
                onClick={addEtape}
                className="border-indigo-200 text-indigo-600 hover:bg-indigo-50 w-full sm:w-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une tâche
              </Button>
            </CardContent>
          </Card>

          {/* Date du prochain comité - Mobile Optimized */}
          <Card className="border-green-200 bg-white shadow-md">
            <CardHeader className="pb-3 sm:pb-4">
              <CardTitle className="flex items-center text-green-700 text-base sm:text-lg">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                6. Prochain comité
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Input
                type="date"
                value={pv.prochainComite}
                onChange={(e) => setPV(prev => ({ ...prev, prochainComite: e.target.value }))}
                className="border-green-200 focus:border-green-400 text-sm sm:text-base"
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PVForm;